import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../utils/api';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { useDebounce } from '../../hooks/useDebounce';
import Pagination from '../../components/common/Pagination';
import { MatchRatingTrendChart } from '../../components/MatchRatingTrendChart';
import { getCalendarDateForMatchDay } from '../../utils/matchFeedbackSchedule';
import { isDaysMatchType } from '../../utils/matchTypeUi';
import { isAbandonedMatchFeedback } from '../../utils/matchFeedbackAbandoned';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';

const datePresets = [
  { label: 'Overall', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'Last Week', value: 'lastWeek' },
  { label: 'This Month', value: 'month' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Year', value: 'year' },
  { label: 'Custom Range', value: 'custom' },
];

const getNestedPayload = (response) => response?.data?.data || response?.data || response || {};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isUnratedOrNaScore = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'string') {
    const t = value.trim().toLowerCase().replace(/\s+/g, '');
    return t === 'n/a' || t === 'na' || t === 'ab';
  }
  return false;
};

const formatParameterCell = (value) => {
  if (isUnratedOrNaScore(value)) return 'NA';
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(1) : 'NA';
};

/** Row-level average: abandoned / unrated rows use null — never coerce API null to 0. */
const formatAverageCell = (totalScore) => {
  if (totalScore === null || totalScore === undefined) return 'NA';
  const n = Number(totalScore);
  return Number.isFinite(n) ? n.toFixed(1) : 'NA';
};

/**
 * Match or day marked abandoned, or no usable numeric scores and no valid total (same idea as API null total).
 */
const inferEmployeeDetailFeedbackAbandoned = (raw, scores = {}, parameterNames = []) => {
  if (!raw || typeof raw !== 'object') return false;
  if (raw.abandoned === true || raw.isAbandoned === true) return true;

  const sl = (v) => String(v ?? '').trim().toLowerCase();
  for (const k of ['matchStatus', 'status', 'feedbackStatus']) {
    if (sl(raw[k]) === 'abandoned') return true;
  }
  const match = raw.matchId || raw.match;
  if (match && typeof match === 'object') {
    for (const k of ['matchStatus', 'status']) {
      if (sl(match[k]) === 'abandoned') return true;
    }
  }

  try {
    if (isAbandonedMatchFeedback(raw)) return true;
  } catch {
    /* ignore */
  }

  const keys =
    Array.isArray(parameterNames) && parameterNames.length > 0
      ? parameterNames
      : Object.keys(scores || {});
  const hasNumericParameterScore = keys.some((name) => {
    const v = scores[name];
    return (
      v !== null &&
      v !== undefined &&
      !isUnratedOrNaScore(v) &&
      Number.isFinite(Number(v))
    );
  });

  const explicitTotal = raw.total ?? raw.average ?? raw.overallRating;
  const totalMissing =
    explicitTotal === null || explicitTotal === undefined || explicitTotal === '';

  if (totalMissing && !hasNumericParameterScore) return true;

  const n = Number(explicitTotal);
  if (!totalMissing && Number.isFinite(n) && n === 0 && !hasNumericParameterScore) return true;

  return false;
};

const toScoreKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, char) => char.toUpperCase())
    .replace(/[^a-z0-9]/g, '');

const normalizeScoreKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getScaleMaxCandidate = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const inferScaleMaxFromPayload = (payload, rawSummaryRows = [], rawRows = []) => {
  const payloadCandidates = [
    payload?.overallOutOf,
    payload?.outOf,
    payload?.ratingOutOf,
    payload?.maxRating,
    payload?.ratingScale,
    payload?.scaleMax,
    payload?.meta?.overallOutOf,
    payload?.meta?.outOf,
    payload?.meta?.ratingOutOf,
    payload?.meta?.maxRating,
    payload?.meta?.ratingScale,
    payload?.meta?.scaleMax,
  ];
  const fromPayload = payloadCandidates.map(getScaleMaxCandidate).find(Boolean);
  if (fromPayload) return fromPayload;

  const rowCandidates = [...rawSummaryRows, ...rawRows].flatMap((row) => [
    row?.overallOutOf,
    row?.outOf,
    row?.ratingOutOf,
    row?.maxRating,
    row?.ratingScale,
    row?.scaleMax,
  ]);
  const fromRows = rowCandidates.map(getScaleMaxCandidate).find(Boolean);
  if (fromRows) return fromRows;

  return null;
};

const inferScaleMaxFromValues = (rows = [], summaryRows = []) => {
  const values = [
    ...rows.flatMap((row) => {
      const parts = [
        ...Object.values(row?.scores || {}).map((value) => toNumber(value, NaN)),
      ];
      if (row?.totalScore !== null && row?.totalScore !== undefined) {
        parts.push(toNumber(row.totalScore, NaN));
      }
      return parts;
    }),
    ...summaryRows.flatMap((row) => [
      ...Object.values(row?.scores || {}).map((value) => toNumber(value, NaN)),
      toNumber(row?.overallRating, NaN),
    ]),
  ].filter((value) => Number.isFinite(value) && value >= 0);

  if (!values.length) return null;

  const maxValue = Math.max(...values);
  if (maxValue > 1) return 5;

  // Never infer "0–1 normalized → stretch to /5" from maxValue === 1 alone: a real 1/5 rating
  // also has max 1 and would be wrongly mapped to 5 via applyScaleToRating(n, 1).
  const looksLikeZeroToOneNormalized =
    maxValue < 1 ||
    values.some((v) => {
      const n = Number(v);
      return Number.isFinite(n) && !Number.isInteger(n);
    }) ||
    values.some((v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 && n < 1;
    });

  if (looksLikeZeroToOneNormalized) return 1;
  return null;
};

const applyScaleToRating = (value, sourceMax) => {
  if (value === null || value === undefined) return value;
  const numeric = toNumber(value, NaN);
  if (!Number.isFinite(numeric)) return value;
  if (!sourceMax || sourceMax === 5) return numeric;
  return (numeric / sourceMax) * 5;
};

const applyScaleToScores = (scores = {}, sourceMax) => {
  if (!scores || typeof scores !== 'object') return scores;
  const scaled = {};
  Object.keys(scores).forEach((key) => {
    const scoreValue = scores[key];
    scaled[key] = scoreValue === null || scoreValue === undefined
      ? scoreValue
      : applyScaleToRating(scoreValue, sourceMax);
  });
  return scaled;
};

const resolveScoreValue = (sourceScores, parameterName, defaultValue = null) => {
  if (!sourceScores || typeof sourceScores !== 'object') return defaultValue;

  const keyByLabel = parameterName;
  const keyByCamel = toScoreKey(parameterName);
  const normalizedTarget = normalizeScoreKey(parameterName);

  const coalesce = (raw) => {
    if (isUnratedOrNaScore(raw)) return null;
    if (raw === undefined) return defaultValue;
    const n = toNumber(raw, NaN);
    return Number.isFinite(n) ? n : defaultValue;
  };

  if (sourceScores[keyByLabel] !== undefined) return coalesce(sourceScores[keyByLabel]);
  if (sourceScores[keyByCamel] !== undefined) return coalesce(sourceScores[keyByCamel]);

  const matchedKey = Object.keys(sourceScores).find(
    (key) => normalizeScoreKey(key) === normalizedTarget
  );
  if (matchedKey) return coalesce(sourceScores[matchedKey]);

  return defaultValue;
};

const normalizeParameterNames = (parameterResponse) => {
  const parameterRows = Array.isArray(parameterResponse?.data)
    ? parameterResponse.data
    : Array.isArray(parameterResponse)
    ? parameterResponse
    : [];

  return parameterRows
    .map((item) =>
      String(
        item?.name || item?.parameter || item?.title || item?.feedbackParameter || ''
      ).trim()
    )
    .filter(Boolean);
};

const DETAIL_ROW_META_KEYS = new Set([
  '_id',
  'id',
  'employeeid',
  'userid',
  'employee',
  'username',
  'employeename',
  'jobtype',
  'jobtypeid',
  'jobtypename',
  'role',
  'roleid',
  'eventname',
  'event',
  'eventid',
  'eventdate',
  'date',
  'createdat',
  'remarks',
  'comment',
  'total',
  'overallrating',
  'scores',
  'averages',
  'totalevents',
]);

const inferParameterNamesFromDetailRow = (row) => {
  if (!row || typeof row !== 'object') return [];
  if (row.scores && typeof row.scores === 'object' && !Array.isArray(row.scores)) {
    return Object.keys(row.scores)
      .map((k) => String(k).trim())
      .filter((k) => k && !DETAIL_ROW_META_KEYS.has(normalizeScoreKey(k)));
  }
  return Object.keys(row)
    .filter((key) => {
      const nk = normalizeScoreKey(key);
      if (!nk || DETAIL_ROW_META_KEYS.has(nk)) return false;
      const v = row[key];
      return v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
    })
    .map((k) => String(k).trim());
};

const getDetailScoreSource = (row) => {
  if (row.scores && typeof row.scores === 'object' && !Array.isArray(row.scores)) {
    return row.scores;
  }
  return row;
};

const buildScoresForParameters = (row, parameterNames) => {
  const source = getDetailScoreSource(row);
  const scores = {};
  parameterNames.forEach((name) => {
    scores[name] = resolveScoreValue(source, name, null);
  });
  return scores;
};

const resolveMemberId = (member) =>
  member?.employeeId ||
  member?.userId ||
  member?.memberId ||
  member?.employee?._id ||
  member?.employee?.id ||
  member?.member?._id ||
  member?.member?.id ||
  member?.user?._id ||
  member?.user?.id ||
  member?._id ||
  member?.id;

const resolveMemberRole = (member) =>
  member?.role?.name ||
  member?.roleName ||
  member?.jobTypeName ||
  member?.jobType ||
  member?.role ||
  '';

const extractEventTeamId = (row) =>
  String(row?.eventTeamId || row?._id || row?.id || '').trim();

const buildEventTeamRolesLookup = (eventTeamsRows = []) => {
  const lookup = new Map();

  eventTeamsRows.forEach((row) => {
    const eventTeamId = extractEventTeamId(row);
    if (!eventTeamId) return;

    const members = Array.isArray(row?.teamMembers)
      ? row.teamMembers
      : Array.isArray(row?.members)
      ? row.members
      : [];

    const memberRoleMap = lookup.get(eventTeamId) || new Map();
    members.forEach((member) => {
      const employeeId = String(resolveMemberId(member) || '').trim();
      const role = String(resolveMemberRole(member) || '').trim();
      if (!employeeId || !role) return;
      const existingRoles = memberRoleMap.get(employeeId) || [];
      if (!existingRoles.some((item) => normalizeText(item) === normalizeText(role))) {
        existingRoles.push(role);
      }
      memberRoleMap.set(employeeId, existingRoles);
    });

    lookup.set(eventTeamId, memberRoleMap);
  });

  return lookup;
};

const buildEventTeamMetaLookup = (eventTeamsRows = []) => {
  const lookup = new Map();
  eventTeamsRows.forEach((row) => {
    const eventTeamId = extractEventTeamId(row);
    if (!eventTeamId) return;
    lookup.set(eventTeamId, {
      teamName: String(row?.teamName || row?.team || row?.eventName || '').trim(),
      matchName: String(
        row?.matchName || row?.match?.matchName || row?.matchDetails?.matchName || ''
      ).trim(),
    });
  });
  return lookup;
};

const applyEventTeamRoleMapping = (detailRows = [], eventTeamRolesLookup) => {
  if (!Array.isArray(detailRows) || !detailRows.length || !eventTeamRolesLookup?.size) {
    return detailRows;
  }

  const groupedRows = new Map();
  detailRows.forEach((row, index) => {
    const eventTeamId = extractEventTeamId(row);
    const employeeId = String(row?.employeeId || row?.userId || '').trim();
    if (!eventTeamId || !employeeId) return;
    const key = `${eventTeamId}::${employeeId}`;
    const existing = groupedRows.get(key) || [];
    existing.push({ row, index });
    groupedRows.set(key, existing);
  });

  if (!groupedRows.size) return detailRows;

  const nextRows = [...detailRows];
  groupedRows.forEach((entries, key) => {
    const [eventTeamId, employeeId] = key.split('::');
    const memberRoleMap = eventTeamRolesLookup.get(eventTeamId);
    const roles = memberRoleMap?.get(employeeId) || [];
    if (!roles.length) return;

    const nonLeaderRoles = roles.filter(
      (role) => normalizeText(role) !== normalizeText('Team Leader')
    );
    const fallbackRole = nonLeaderRoles[0] || roles[0] || '';
    entries.forEach(({ row, index }) => {
      const currentRole = String(row?.jobTypeName || row?.jobType || row?.role || '').trim();
      const scoreValues = Object.values(row?.scores || {});
      const hasAnyNumericScore = scoreValues.some(
        (value) => typeof value === 'number' && Number.isFinite(value)
      );
      const isOverallOnlyTeamLeaderRow =
        normalizeText(currentRole) === normalizeText('Team Leader') && !hasAnyNumericScore;
      if (isOverallOnlyTeamLeaderRow) return;
      if (
        fallbackRole && (
          !currentRole ||
          normalizeText(currentRole) === normalizeText('Team Leader') ||
          !nonLeaderRoles.some((role) => normalizeText(role) === normalizeText(currentRole))
        )
      ) {
        nextRows[index] = {
          ...row,
          jobTypeName: fallbackRole,
          jobType: fallbackRole,
        };
      }
    });
  });

  return nextRows;
};

const resolveEventJobTypeFromRow = (row) => {
  const directRole =
    row.eventJobType ||
    row.eventJobTypeName ||
    row.eventRole ||
    row.eventRoleName ||
    row.assignedRole ||
    row.teamRole ||
    row.feedbackForRole ||
    row.event?.jobTypeName ||
    row.event?.jobType?.name ||
    row.event?.jobType ||
    row.event?.role?.name ||
    row.event?.role ||
    '';

  if (String(directRole).trim()) {
    return String(directRole).trim();
  }

  const sourceMembers = [
    ...(Array.isArray(row.teamMembers) ? row.teamMembers : []),
    ...(Array.isArray(row.members) ? row.members : []),
    ...(Array.isArray(row.event?.teamMembers) ? row.event.teamMembers : []),
    ...(Array.isArray(row.event?.members) ? row.event.members : []),
  ];

  if (!sourceMembers.length) return '';

  const rowEmployeeId =
    row.employeeId || row.userId || row.employee?._id || row.employee?.id || row.memberId;

  if (rowEmployeeId) {
    const matchedMember = sourceMembers.find(
      (member) => String(resolveMemberId(member) || '') === String(rowEmployeeId)
    );
    const matchedRole = resolveMemberRole(matchedMember);
    if (String(matchedRole).trim()) return String(matchedRole).trim();
  }

  // Do not guess by taking the first member role; that can mislabel rows.
  // Let callers fallback to row.jobType / row.role from the API payload.
  return '';
};

const normalizeRow = (row, index, parameterNames, eventTeamMetaLookup = new Map()) => {
  const scores = buildScoresForParameters(row, parameterNames);
  const availableScores = parameterNames
    .map((name) => scores[name])
    .filter((value) => Number.isFinite(Number(value)))
    .map((value) => Number(value));
  const fallbackTotal =
    availableScores.length > 0
      ? availableScores.reduce((acc, value) => acc + value, 0) / availableScores.length
      : 0;
  const abandonedRow = inferEmployeeDetailFeedbackAbandoned(row, scores, parameterNames);
  const eventJobType = resolveEventJobTypeFromRow(row);
  const eventTeamMeta = eventTeamMetaLookup.get(extractEventTeamId(row)) || {};
  const matchName = String(
    row.matchName ||
      row.eventName ||
      row.event?.name ||
      row.match?.matchName ||
      eventTeamMeta.matchName ||
      '-'
  ).trim() || '-';
  const teamName = String(
    row.teamName ||
      row.eventTeamName ||
      row.event?.teamName ||
      row.team?.teamName ||
      row.team?.name ||
      eventTeamMeta.teamName ||
      '-'
  ).trim() || '-';
  const eventJobTypeId =
    row.eventJobTypeId ||
    row.eventRoleId ||
    row.assignedRoleId ||
    row.event?.role?._id ||
    row.event?.role?.id ||
    row.event?.jobTypeId ||
    row.event?.jobType?._id ||
    row.event?.jobType?.id;
  const sourceMembers = [
    ...(Array.isArray(row.teamMembers) ? row.teamMembers : []),
    ...(Array.isArray(row.members) ? row.members : []),
    ...(Array.isArray(row.event?.teamMembers) ? row.event.teamMembers : []),
    ...(Array.isArray(row.event?.members) ? row.event.members : []),
  ];
  const memberRoles = sourceMembers
    .map((member) => ({
      id: String(resolveMemberId(member) || ''),
      role: String(resolveMemberRole(member) || '').trim(),
    }))
    .filter((member) => member.id || member.role);

  return {
    id: String(row._id || row.id || `${row.eventId || 'event'}-${index}`),
    employeeName: row.employeeName || row.userName || row.employee?.name || '-',
    employeeId: String(row.employeeId || row.userId || row.employee?._id || ''),
    // Prefer employee-level role from report payload; event-level role is only a fallback.
    jobType: row.jobTypeName || row.jobType || row.role || eventJobType || '-',
    jobTypeId: String(row.jobTypeId || row.roleId || row.jobType?._id || eventJobTypeId || ''),
    matchName,
    teamName,
    matchType: row.matchType || row.match?.matchType || '-',
    feedbackDay: toNumber(row.feedbackDay, null),
    eventName: matchName,
    startDate: getFeedbackDateForDisplay(
      row.matchStartDate ||
        row.startDate ||
        row.eventDate ||
        row.date ||
        row.event?.date ||
        row.createdAt,
      row.matchType || row.match?.matchType || '',
      row.feedbackDay
    ),
    scores,
    totalScore: (() => {
      if (abandonedRow) return null;
      const explicitTotal = row.total ?? row.average ?? row.overallRating;
      if (explicitTotal !== null && explicitTotal !== undefined && explicitTotal !== '') {
        const n = Number(explicitTotal);
        if (Number.isFinite(n)) return n;
      }
      return fallbackTotal > 0 ? fallbackTotal : null;
    })(),
    remarks: row.remarks || row.comment || '-',
    memberRoles,
  };
};

const getSummaryScoreSource = (row) => {
  if (row.scores && typeof row.scores === 'object' && !Array.isArray(row.scores)) {
    return row.scores;
  }
  if (row.averages && typeof row.averages === 'object' && !Array.isArray(row.averages)) {
    return row.averages;
  }
  return row;
};

const inferParameterNamesFromSummaryRow = (row) => {
  if (!row || typeof row !== 'object') return [];
  const source = getSummaryScoreSource(row);
  if (!source || typeof source !== 'object') return [];
  return inferParameterNamesFromDetailRow(
    source.scores && typeof source.scores === 'object' ? source : { scores: source }
  );
};

const normalizeSummaryRow = (row, index, parameterNames) => {
  const source = getSummaryScoreSource(row);
  const scores = {};
  parameterNames.forEach((name) => {
    scores[name] = resolveScoreValue(source, name, null);
  });

  return {
    id: String(row.employeeId || row.userId || index),
    employeeId: String(row.employeeId || row.userId || ''),
    employeeName: row.employeeName || row.userName || '-',
    jobTypeId: String(row.jobTypeId || row.roleId || ''),
    jobTypeName: row.jobTypeName || row.jobType || row.role || '-',
    totalEvents: toNumber(row.totalEvents),
    scores,
    overallRating: toNumber(row.overallRating),
  };
};

const buildEmployeeOptions = (summaryItems = []) =>
  Array.from(
    new Map(
      summaryItems
        .filter((item) => item.employeeId)
        .map((item) => [item.employeeId, { id: item.employeeId, name: item.employeeName }])
    ).values()
  );

/** Options for the employee dropdown from GET /users (always available even when summary API rejects unscoped calls). */
const buildEmployeeOptionsFromUsersResponse = (response) => {
  if (!response?.success || !Array.isArray(response.data)) return [];
  return response.data
    .map((user) => ({
      id: String(user._id || user.id || '').trim(),
      name: String(user.name || user.fullName || user.email || 'User').trim() || 'User',
    }))
    .filter((item) => item.id);
};

const mergeEmployeeSelectOptions = (lists) => {
  const map = new Map();
  lists.forEach((list) => {
    (list || []).forEach((item) => {
      if (!item?.id) return;
      const id = String(item.id);
      if (!map.has(id)) map.set(id, { id, name: item.name || id });
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
  );
};

const normalizeJobTypesResponse = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.data?.data)
    ? response.data.data
    : Array.isArray(response?.results)
    ? response.results
    : Array.isArray(response)
    ? response
    : [];

  return rows.map((item, index) => {
    if (typeof item === 'string') {
      return { id: String(index + 1), name: item.trim() || '-' };
    }
    return {
      id: String(item._id || item.id || index + 1),
      name: item.name || item.jobType || item.title || '-',
    };
  });
};

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatParameterLabel = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const withSpaces = text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const formatFeedbackDay = (value) => {
  const day = toNumber(value, NaN);
  if (!Number.isFinite(day) || day <= 0) return '-';
  return `Day ${Math.trunc(day)}`;
};

const formatOverallRatingValue = (value) => {
  const n = toNumber(value, NaN);
  if (!Number.isFinite(n)) return '0.0';
  return n.toFixed(1);
};

const getFeedbackDateForDisplay = (matchStartDate, matchType, feedbackDay) => {
  if (!isDaysMatchType(matchType)) return matchStartDate;
  const day = toNumber(feedbackDay, NaN);
  if (!Number.isFinite(day) || day < 1) return matchStartDate;
  return getCalendarDateForMatchDay(matchStartDate, day) || matchStartDate;
};

const formatMatchNameWithDay = (row) => {
  const base = String(row?.matchName || row?.eventName || '-').trim() || '-';
  const matchType = String(row?.matchType || '').trim();
  const dayLabel = formatFeedbackDay(row?.feedbackDay);
  if (!isDaysMatchType(matchType) || dayLabel === '-') return base;
  return `${base} (${dayLabel})`;
};

const getStartOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getEndOfDay = (value) => {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

const isDateInSelectedRange = (value, range, from, to) => {
  if (!value || range === 'all') return true;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return false;

  const now = new Date();
  const todayStart = getStartOfDay(now);
  const todayEnd = getEndOfDay(now);

  if (range === 'today') return target >= todayStart && target <= todayEnd;

  if (range === 'week') {
    const start = getStartOfDay(now);
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + diff);
    const end = getEndOfDay(start);
    end.setDate(start.getDate() + 6);
    return target >= start && target <= end;
  }

  if (range === 'lastWeek') {
    const thisWeekStart = getStartOfDay(now);
    const day = thisWeekStart.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    thisWeekStart.setDate(thisWeekStart.getDate() + diff);

    const start = getStartOfDay(thisWeekStart);
    start.setDate(thisWeekStart.getDate() - 7);
    const end = getEndOfDay(start);
    end.setDate(start.getDate() + 6);
    return target >= start && target <= end;
  }

  if (range === 'month') {
    const start = getStartOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    const end = getEndOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    return target >= start && target <= end;
  }

  if (range === 'lastMonth') {
    const start = getStartOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    const end = getEndOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
    return target >= start && target <= end;
  }

  if (range === 'year') {
    const start = getStartOfDay(new Date(now.getFullYear(), 0, 1));
    const end = getEndOfDay(new Date(now.getFullYear(), 11, 31));
    return target >= start && target <= end;
  }

  if (range === 'custom') {
    if (!from || !to) return false;
    const start = getStartOfDay(from);
    const end = getEndOfDay(to);
    return target >= start && target <= end;
  }

  return true;
};

const formatIsoDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Bounds for GET /event-feedback/employee-summary (startDateFrom / startDateTo). */
const computeStartDateRangeForApi = (range, from, to) => {
  if (!range || range === 'all') return null;

  const now = new Date();

  if (range === 'custom') {
    if (!from || !to) return null;
    return { startDateFrom: from, startDateTo: to };
  }

  if (range === 'today') {
    const start = getStartOfDay(now);
    const end = getEndOfDay(now);
    return { startDateFrom: formatIsoDate(start), startDateTo: formatIsoDate(end) };
  }

  if (range === 'week') {
    const start = getStartOfDay(now);
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + diff);
    const end = getEndOfDay(start);
    end.setDate(start.getDate() + 6);
    return { startDateFrom: formatIsoDate(start), startDateTo: formatIsoDate(end) };
  }

  if (range === 'lastWeek') {
    const thisWeekStart = getStartOfDay(now);
    const day = thisWeekStart.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    thisWeekStart.setDate(thisWeekStart.getDate() + diff);

    const start = getStartOfDay(thisWeekStart);
    start.setDate(thisWeekStart.getDate() - 7);
    const end = getEndOfDay(start);
    end.setDate(start.getDate() + 6);
    return { startDateFrom: formatIsoDate(start), startDateTo: formatIsoDate(end) };
  }

  if (range === 'month') {
    const start = getStartOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    const end = getEndOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    return { startDateFrom: formatIsoDate(start), startDateTo: formatIsoDate(end) };
  }

  if (range === 'lastMonth') {
    const start = getStartOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    const end = getEndOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
    return { startDateFrom: formatIsoDate(start), startDateTo: formatIsoDate(end) };
  }

  if (range === 'year') {
    const start = getStartOfDay(new Date(now.getFullYear(), 0, 1));
    const end = getEndOfDay(new Date(now.getFullYear(), 11, 31));
    return { startDateFrom: formatIsoDate(start), startDateTo: formatIsoDate(end) };
  }

  return null;
};

const tableSortToApiParams = (tableSort) => {
  const direction = tableSort?.direction === 'asc' ? 'asc' : 'desc';
  const key = tableSort?.key || 'startDate';
  if (String(key).startsWith('score:')) {
    return { sortBy: String(key).slice(6), sortOrder: direction };
  }
  return { sortBy: key, sortOrder: direction };
};

const rowSortTimestamp = (value) => {
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
};

/** Null / missing numeric scores sort after finite values. */
const compareNullableNumbers = (a, b) => {
  const aOk = Number.isFinite(a);
  const bOk = Number.isFinite(b);
  if (!aOk && !bOk) return 0;
  if (!aOk) return 1;
  if (!bOk) return -1;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const compareEmployeeReportRows = (a, b, sortKey) => {
  const key = sortKey || 'startDate';
  let cmp = 0;

  if (String(key).startsWith('score:')) {
    const param = String(key).slice(6);
    const av = a.scores?.[param];
    const bv = b.scores?.[param];
    const an =
      av === null || av === undefined ? NaN : toNumber(av, NaN);
    const bn =
      bv === null || bv === undefined ? NaN : toNumber(bv, NaN);
    cmp = compareNullableNumbers(an, bn);
  } else if (key === 'totalScore') {
    const av =
      a.totalScore === null || a.totalScore === undefined
        ? NaN
        : toNumber(a.totalScore, NaN);
    const bv =
      b.totalScore === null || b.totalScore === undefined
        ? NaN
        : toNumber(b.totalScore, NaN);
    cmp = compareNullableNumbers(av, bv);
  } else if (key === 'startDate') {
    cmp = rowSortTimestamp(a.startDate) - rowSortTimestamp(b.startDate);
  } else if (key === 'matchName') {
    cmp = String(a.matchName || a.eventName || '').localeCompare(
      String(b.matchName || b.eventName || ''),
      undefined,
      { sensitivity: 'base', numeric: true }
    );
  } else if (key === 'teamName') {
    cmp = String(a.teamName || '').localeCompare(String(b.teamName || ''), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  } else if (key === 'jobType') {
    cmp = String(a.jobType || '').localeCompare(String(b.jobType || ''), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  } else if (key === 'matchType') {
    cmp = String(a.matchType || '').localeCompare(String(b.matchType || ''), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  } else if (key === 'feedbackDay') {
    cmp = compareNullableNumbers(toNumber(a.feedbackDay, NaN), toNumber(b.feedbackDay, NaN));
  } else if (key === 'remarks') {
    cmp = String(a.remarks || '').localeCompare(String(b.remarks || ''), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  }

  if (cmp !== 0) return cmp;
  return String(a.id).localeCompare(String(b.id));
};

const sortEmployeeReportRows = (list, sortKey, direction) => {
  const mult = direction === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => mult * compareEmployeeReportRows(a, b, sortKey));
};

/** Total detail rows for pagination (backend may return total / totalCount / pagination.total / …). */
const extractEmployeeSummaryDetailTotal = (payload, responseRoot, detailsLength) => {
  const objs = [];
  const add = (o) => {
    if (o && typeof o === 'object' && !Array.isArray(o)) objs.push(o);
  };
  add(payload);
  add(responseRoot);
  add(payload?.data);
  add(responseRoot?.data);
  const keys = ['total', 'totalCount', 'totalItems', 'count', 'detailsTotal', 'detailTotal'];
  for (const obj of objs) {
    for (const k of keys) {
      const n = Number(obj[k]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    const p = obj.pagination;
    if (p && Number.isFinite(Number(p.total))) return Number(p.total);
    const m = obj.meta;
    if (m && Number.isFinite(Number(m.total))) return Number(m.total);
  }
  return detailsLength;
};

const normalizeEmployeeSummaryFetchResult = (response, eventTeamsResponse, configuredParameters) => {
  const payload = getNestedPayload(response);
  const root = response || {};

  const rawSummaryRows = Array.isArray(payload.summary)
    ? payload.summary
    : Array.isArray(payload.summaries)
    ? payload.summaries
    : [];
  const rawRows = Array.isArray(payload.details)
    ? payload.details
    : Array.isArray(payload.rows)
    ? payload.rows
    : Array.isArray(payload.events)
    ? payload.events
    : [];
  const rawEventTeams = Array.isArray(eventTeamsResponse?.data)
    ? eventTeamsResponse.data
    : Array.isArray(eventTeamsResponse)
    ? eventTeamsResponse
    : [];
  const eventTeamRolesLookup = buildEventTeamRolesLookup(rawEventTeams);
  const eventTeamMetaLookup = buildEventTeamMetaLookup(rawEventTeams);
  const roleAdjustedRawRows = applyEventTeamRoleMapping(rawRows, eventTeamRolesLookup);

  const inferredFromDetails = inferParameterNamesFromDetailRow(roleAdjustedRawRows[0]);
  const inferredFromSummary = inferParameterNamesFromSummaryRow(rawSummaryRows[0]);
  const inferredColumns =
    inferredFromDetails.length > 0 ? inferredFromDetails : inferredFromSummary;
  const columnNames =
    inferredColumns.length > 0 ? inferredColumns : configuredParameters;

  const normalizedSummaryRows = rawSummaryRows.map((item, index) =>
    normalizeSummaryRow(item, index, columnNames)
  );
  const normalizedRows = roleAdjustedRawRows.map((row, index) =>
    normalizeRow(row, index, columnNames, eventTeamMetaLookup)
  );
  const sourceScaleMax =
    inferScaleMaxFromPayload(payload, rawSummaryRows, roleAdjustedRawRows) ||
    inferScaleMaxFromValues(normalizedRows, normalizedSummaryRows);
  const scaledSummaryRows =
    sourceScaleMax && sourceScaleMax !== 5
      ? normalizedSummaryRows.map((row) => ({
          ...row,
          scores: applyScaleToScores(row.scores, sourceScaleMax),
          overallRating: applyScaleToRating(row.overallRating, sourceScaleMax),
        }))
      : normalizedSummaryRows;
  const scaledRows =
    sourceScaleMax && sourceScaleMax !== 5
      ? normalizedRows.map((row) => ({
          ...row,
          scores: applyScaleToScores(row.scores, sourceScaleMax),
          totalScore: applyScaleToRating(row.totalScore, sourceScaleMax),
        }))
      : normalizedRows;

  const detailTotal = extractEmployeeSummaryDetailTotal(payload, root, scaledRows.length);

  return {
    reportColumns: columnNames,
    scaledRows,
    scaledSummaryRows,
    detailTotal,
  };
};

const EmployeeFeedbackReportPage = () => {
  const { jobTypesResponse, feedbackParameterRows, loading: referenceDataLoading } =
    useReferenceData();

  const jobTypes = useMemo(
    () => normalizeJobTypesResponse(jobTypesResponse || {}),
    [jobTypesResponse]
  );

  const configuredParameters = useMemo(() => {
    if (referenceDataLoading) return null;
    return normalizeParameterNames({ data: feedbackParameterRows });
  }, [feedbackParameterRows, referenceDataLoading]);

  const [employeeId, setEmployeeId] = useState('');
  const [jobTypeId, setJobTypeId] = useState('');
  const [jobTypeFilterName, setJobTypeFilterName] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 400);
  const [tableSort, setTableSort] = useState({ key: 'startDate', direction: 'desc' });
  const [loading, setLoading] = useState(false);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [, setErrorMessage] = useState('');
  const [rows, setRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [reportColumns, setReportColumns] = useState([]);
  const [isSendingPdfMail, setIsSendingPdfMail] = useState(false);
  const [tableCurrentPage, setTableCurrentPage] = useState(1);
  const [tableItemsPerPage, setTableItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [detailTotalCount, setDetailTotalCount] = useState(0);
  const [rowsForChart, setRowsForChart] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  const rowsRef = useRef([]);
  const lastSuccessfulEmployeeIdRef = useRef('');
  const employeeChartScopeKeyRef = useRef('');
  const lastSummaryQueryRef = useRef(null);
  const tableSortRef = useRef(tableSort);
  const filterRef = useRef({});
  const fetchEmployeeReportRef = useRef(async () => {});
  /** event-teams is only used for role/team name mapping; same list for all employees—fetch once. */
  const eventTeamsCacheRef = useRef(null);
  const eventTeamsLoadPromiseRef = useRef(null);
  const getEventTeamsCached = useCallback(async () => {
    if (eventTeamsCacheRef.current !== null) {
      return eventTeamsCacheRef.current;
    }
    if (eventTeamsLoadPromiseRef.current) {
      return eventTeamsLoadPromiseRef.current;
    }
    const p = api
      .getEventTeams()
      .then((res) => {
        eventTeamsCacheRef.current = res;
        eventTeamsLoadPromiseRef.current = null;
        return res;
      })
      .catch((err) => {
        eventTeamsLoadPromiseRef.current = null;
        throw err;
      });
    eventTeamsLoadPromiseRef.current = p;
    return p;
  }, []);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Fetch settings on mount and update tableItemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== tableItemsPerPage) {
        setTableItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  tableSortRef.current = tableSort;
  filterRef.current = {
    employeeId,
    jobTypeId,
    dateRange,
    fromDate,
    toDate,
    debouncedSearchText,
    configuredParameters,
    tableCurrentPage,
    tableItemsPerPage,
  };

  useEffect(() => {
    let isMounted = true;

    const loadEmployeeOptions = async () => {
      try {
        const allUsersResponse = await api
          .getUsers({ limit: 1000, isActive: 'true' })
          .catch(() => null);

        const fromUsers = mergeEmployeeSelectOptions([
          buildEmployeeOptionsFromUsersResponse(allUsersResponse || {}),
        ]);

        if (!isMounted) return;
        setEmployees(fromUsers);
      } catch {
        if (!isMounted) return;
      }
    };

    loadEmployeeOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  fetchEmployeeReportRef.current = async ({ isAlive }) => {
    const f = filterRef.current;
    const finishFlags = () => {
      if (isAlive()) {
        setLoading(false);
        setTableRefreshing(false);
      }
    };

    if (!f.employeeId) {
      if (isAlive()) {
        setRows([]);
        setSummaryRows([]);
        setReportColumns([]);
        setDetailTotalCount(0);
        setRowsForChart([]);
        employeeChartScopeKeyRef.current = '';
        lastSummaryQueryRef.current = null;
        lastSuccessfulEmployeeIdRef.current = '';
        finishFlags();
      }
      return;
    }

    if (f.configuredParameters === null) {
      if (isAlive()) {
        setLoading(true);
        setTableRefreshing(false);
      }
      return;
    }

    try {
      setErrorMessage('');

      const employeeContextUnchanged =
        lastSuccessfulEmployeeIdRef.current === String(f.employeeId);
      const reuseVisibleTable =
        rowsRef.current.length > 0 && employeeContextUnchanged;
      if (reuseVisibleTable) {
        setTableRefreshing(true);
      } else {
        setLoading(true);
      }

      const dateBounds = computeStartDateRangeForApi(f.dateRange, f.fromDate, f.toDate);
      const { sortBy, sortOrder } = tableSortToApiParams(tableSortRef.current);
      const page = Math.max(1, Number(f.tableCurrentPage) || 1);
      const limit = Math.max(1, Number(f.tableItemsPerPage) || 10);
      const query = {
        userId: f.employeeId || undefined,
        employeeId: f.employeeId || undefined,
        sortBy,
        sortOrder,
        page,
        limit,
      };

      if (dateBounds) {
        query.startDateFrom = dateBounds.startDateFrom;
        query.startDateTo = dateBounds.startDateTo;
      }

      if (f.jobTypeId) {
        query.jobTypeId = f.jobTypeId;
      }

      const matchFilter = String(f.debouncedSearchText || '').trim();
      if (matchFilter) {
        query.matchName = matchFilter;
      }

      if (f.dateRange === 'custom' && (!f.fromDate || !f.toDate)) {
        if (isAlive()) {
          setRows([]);
          setSummaryRows([]);
          setReportColumns([]);
          setDetailTotalCount(0);
          setRowsForChart([]);
          lastSummaryQueryRef.current = null;
          setErrorMessage('Select both from and to date for custom range.');
        }
        finishFlags();
        return;
      }

      const chartScopeKey = [
        f.employeeId,
        f.jobTypeId,
        f.dateRange,
        f.fromDate,
        f.toDate,
        f.debouncedSearchText,
      ].join('|');
      const chartScopeChanged = employeeChartScopeKeyRef.current !== chartScopeKey;
      if (chartScopeChanged && isAlive()) {
        setRowsForChart([]);
      }

      lastSummaryQueryRef.current = { ...query };

      const [response, eventTeamsResponse] = await Promise.all([
        api.getEmployeeFeedbackSummary(query),
        getEventTeamsCached(),
      ]);

      if (!isAlive()) return;

      const {
        reportColumns: columnNames,
        scaledRows,
        scaledSummaryRows,
        detailTotal,
      } = normalizeEmployeeSummaryFetchResult(response, eventTeamsResponse, f.configuredParameters);

      if (!isAlive()) return;
      setReportColumns(columnNames);
      setRows(scaledRows);
      setSummaryRows(scaledSummaryRows);
      setDetailTotalCount(detailTotal);
      lastSuccessfulEmployeeIdRef.current = String(f.employeeId);
      if (chartScopeChanged && isAlive()) {
        employeeChartScopeKeyRef.current = chartScopeKey;
      }

      if (chartScopeChanged && isAlive()) {
        if (!detailTotal || detailTotal <= 0) {
          setRowsForChart([]);
        } else {
          const scopeLocked = chartScopeKey;
          void (async () => {
            try {
              setChartLoading(true);
              const chartQuery = {
                ...query,
                page: 1,
                limit: Math.min(2000, detailTotal),
                sortBy: 'startDate',
                sortOrder: 'asc',
              };
              const [chartResponse, chartEventTeams] = await Promise.all([
                api.getEmployeeFeedbackSummary(chartQuery),
                getEventTeamsCached(),
              ]);
              if (!isAlive() || employeeChartScopeKeyRef.current !== scopeLocked) return;
              const { scaledRows: chartScaled } = normalizeEmployeeSummaryFetchResult(
                chartResponse,
                chartEventTeams,
                f.configuredParameters
              );
              if (!isAlive() || employeeChartScopeKeyRef.current !== scopeLocked) return;
              setRowsForChart(chartScaled);
            } catch {
              if (isAlive() && employeeChartScopeKeyRef.current === scopeLocked) {
                setRowsForChart([]);
              }
            } finally {
              if (isAlive() && employeeChartScopeKeyRef.current === scopeLocked) {
                setChartLoading(false);
              }
            }
          })();
        }
      }
      setEmployees((prev) =>
        mergeEmployeeSelectOptions([prev, buildEmployeeOptions(scaledSummaryRows)])
      );
    } catch (error) {
      if (!isAlive()) return;
      setRows([]);
      setSummaryRows([]);
      setReportColumns([]);
      setDetailTotalCount(0);
      setRowsForChart([]);
      employeeChartScopeKeyRef.current = '';
      lastSummaryQueryRef.current = null;
      lastSuccessfulEmployeeIdRef.current = '';
      setErrorMessage(error.message || 'Failed to load employee feedback report');
    } finally {
      finishFlags();
    }
  };

  /** Filters, sort, pagination — server returns one page of details + total count. */
  useEffect(() => {
    let alive = true;
    const isAlive = () => alive;
    void fetchEmployeeReportRef.current({ isAlive });
    return () => {
      alive = false;
    };
  }, [
    employeeId,
    jobTypeId,
    dateRange,
    fromDate,
    toDate,
    configuredParameters,
    debouncedSearchText,
    tableSort.key,
    tableSort.direction,
    tableCurrentPage,
    tableItemsPerPage,
  ]);

  const dateRangeRows = useMemo(
    () => rows.filter((row) => isDateInSelectedRange(row.startDate, dateRange, fromDate, toDate)),
    [rows, dateRange, fromDate, toDate]
  );
  const selectedEmployeeId = String(employeeId || '');

  const dateRangeRowsForSelectedEmployee = useMemo(() => {
    if (!selectedEmployeeId) return [];
    // Backend details are employee-level rows; use strict employeeId matching.
    return dateRangeRows.filter((row) => String(row.employeeId || '') === selectedEmployeeId);
  }, [dateRangeRows, selectedEmployeeId]);

  const employeeRows = useMemo(() => dateRangeRowsForSelectedEmployee, [dateRangeRowsForSelectedEmployee]);

  const selectedJobTypeName = useMemo(() => {
    if (jobTypeFilterName) return jobTypeFilterName;
    if (!jobTypeId) return '';
    const matched = jobTypes.find((item) => String(item.id) === String(jobTypeId));
    return matched?.name || '';
  }, [jobTypes, jobTypeId, jobTypeFilterName]);

  const jobTypeRows = useMemo(() => {
    if (!jobTypeId) return employeeRows;
    const selectedName = normalizeText(selectedJobTypeName);
    const selectedId = String(jobTypeId);
    return employeeRows.filter((row) => {
      const rowJobTypeId = String(row.jobTypeId || '');
      if (rowJobTypeId && rowJobTypeId === selectedId) return true;
      if (!selectedName) return false;
      return normalizeText(row.jobType) === selectedName;
    });
  }, [employeeRows, jobTypeId, selectedJobTypeName]);

  /* Detail rows: API may ignore sortBy; we sort client-side so headers match the grid. */
  const filteredRows = useMemo(() => [...jobTypeRows], [jobTypeRows]);

  const visibleRowsBase = useMemo(() => {
    if (!selectedJobTypeName) return filteredRows;
    const selectedName = normalizeText(selectedJobTypeName);
    return filteredRows.filter((row) => normalizeText(row.jobType) === selectedName);
  }, [filteredRows, selectedJobTypeName]);

  const visibleRows = useMemo(
    () =>
      sortEmployeeReportRows(visibleRowsBase, tableSort.key, tableSort.direction),
    [visibleRowsBase, tableSort.key, tableSort.direction]
  );

  const tableTotalItems = employeeId ? detailTotalCount : 0;
  const tableTotalPages = Math.max(
    1,
    Math.ceil(tableTotalItems / Math.max(1, tableItemsPerPage))
  );
  const tableSafePage = Math.min(tableCurrentPage, tableTotalPages);

  useEffect(() => {
    setTableCurrentPage(1);
  }, [employeeId, jobTypeId, jobTypeFilterName, dateRange, fromDate, toDate, debouncedSearchText]);

  useEffect(() => {
    setTableCurrentPage((prev) => Math.min(prev, tableTotalPages));
  }, [tableTotalPages]);

  const strictEmployeeJobRows = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const selectedJobTypeId = String(jobTypeId || '');
    const selectedJobType = normalizeText(selectedJobTypeName);
    const byEmployee = dateRangeRowsForSelectedEmployee;

    if (!selectedJobTypeId && !selectedJobType) return byEmployee;

    return byEmployee.filter((row) => {
      const rowJobTypeId = String(row.jobTypeId || '');
      if (selectedJobTypeId && rowJobTypeId) {
        return rowJobTypeId === selectedJobTypeId;
      }
      if (selectedJobType) {
        return normalizeText(row.jobType) === selectedJobType;
      }
      return true;
    });
  }, [dateRangeRowsForSelectedEmployee, selectedEmployeeId, jobTypeId, selectedJobTypeName]);

  const handleColumnSort = (key) => {
    setTableCurrentPage(1);
    setTableSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (key) => {
    if (tableSort.key !== key) return '↕';
    return tableSort.direction === 'asc' ? '↑' : '↓';
  };

  const renderSortableHeader = (sortKey, label, title) => {
    const isActive = tableSort.key === sortKey;
    const leftAligned = ['matchName', 'teamName', 'jobType', 'remarks'].includes(sortKey);
    return (
      <th
        key={sortKey}
        style={{
          textAlign: leftAligned ? 'left' : 'center',
          padding: '9px 10px',
          color: isActive ? '#93c5fd' : '#f8fafc',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'color 0.15s ease',
          whiteSpace: 'nowrap',
        }}
        onClick={() => handleColumnSort(sortKey)}
        title={title}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span>{label}</span>
          <span
            style={{
              color: isActive ? '#93c5fd' : '#cbd5e1',
              fontSize: '10px',
              transition: 'color 0.15s ease',
            }}
          >
            {getSortIcon(sortKey)}
          </span>
        </span>
      </th>
    );
  };

  const summary = useMemo(() => {
    const sourceRows = strictEmployeeJobRows.length > 0 ? strictEmployeeJobRows : jobTypeRows;
    const selectedEmployee = String(employeeId || '');
    const selectedJobTypeId = String(jobTypeId || '');
    const selectedJobType = normalizeText(selectedJobTypeName);

    const matchedSummaryRows = summaryRows.filter((item) => {
      if (String(item.employeeId || '') !== selectedEmployee) return false;
      if (selectedJobTypeId) {
        return String(item.jobTypeId || '') === selectedJobTypeId;
      }
      if (selectedJobType) {
        return normalizeText(item.jobTypeName) === selectedJobType;
      }
      return true;
    });

    const keys =
      reportColumns.length > 0
        ? reportColumns
        : sourceRows[0]?.scores
        ? Object.keys(sourceRows[0].scores)
        : [];

    const isServerPaginated =
      detailTotalCount > 0 && detailTotalCount > sourceRows.length;
    /** One scoped summary row only — using [0] when "All Job Types" matches several rows picked the wrong job type and showed 0.0. */
    const summaryMetaRow =
      matchedSummaryRows.length === 1
        ? matchedSummaryRows[0]
        : matchedSummaryRows.length === 0 &&
          isServerPaginated &&
          summaryRows.length === 1
        ? summaryRows[0]
        : null;

    const averagesFromDetailRows = (paramKeys, prev = {}) => {
      const out = { ...prev };
      if (!sourceRows.length) return out;
      paramKeys.forEach((k) => {
        if (out[k] !== null && out[k] !== undefined && Number.isFinite(toNumber(out[k], NaN))) return;
        let sum = 0;
        let n = 0;
        sourceRows.forEach((row) => {
          const v = row.scores?.[k];
          if (v === null || v === undefined || isUnratedOrNaScore(v)) return;
          sum += toNumber(v, 0);
          n += 1;
        });
        if (n) out[k] = sum / n;
      });
      return out;
    };

    const hasAnyValidScore = (row) =>
      keys.some((k) => {
        const value = row.scores?.[k];
        return value !== null && value !== undefined && !isUnratedOrNaScore(value);
      });

    const validOverallRows = sourceRows.filter((row) => hasAnyValidScore(row));
    const validOverallRowsWithAverage = validOverallRows.filter(
      (row) =>
        row.totalScore !== null &&
        row.totalScore !== undefined &&
        Number.isFinite(Number(row.totalScore))
    );

    const overallAverageFromDetailRows =
      validOverallRowsWithAverage.length > 0
        ? validOverallRowsWithAverage.reduce((sum, row) => sum + Number(row.totalScore), 0) /
          validOverallRowsWithAverage.length
        : 0;

    if (isServerPaginated && summaryMetaRow) {
      const m = summaryMetaRow;
      const paramKeys =
        keys.length > 0 ? keys : m.scores && typeof m.scores === 'object' ? Object.keys(m.scores) : [];
      const averagesByParam = {};
      paramKeys.forEach((k) => {
        averagesByParam[k] =
          m.scores && m.scores[k] !== null && m.scores[k] !== undefined
            ? toNumber(m.scores[k], null)
            : null;
      });
      let overallRating = toNumber(m.overallRating, 0);
      if (overallRating <= 0 && overallAverageFromDetailRows > 0) {
        overallRating = overallAverageFromDetailRows;
      }
      const mergedAverages = averagesFromDetailRows(paramKeys, averagesByParam);
      return {
        totalEvents: detailTotalCount,
        averagesByParam: mergedAverages,
        paramKeys,
        overallRating: overallRating > 0 ? overallRating : 0,
        overallOutOf: 5,
      };
    }

    if (!sourceRows.length) {
      return {
        totalEvents: 0,
        averagesByParam: {},
        paramKeys: keys,
        overallRating: 0,
        overallOutOf: 5,
      };
    }

    const sums = Object.fromEntries(keys.map((k) => [k, 0]));
    const counts = Object.fromEntries(keys.map((k) => [k, 0]));
    let totalEvents = 0;
    let overallRatingSum = 0;
    let overallCount = 0;

    sourceRows.forEach((row) => {
      totalEvents += 1;
      if (hasAnyValidScore(row)) {
        if (
          row.totalScore !== null &&
          row.totalScore !== undefined &&
          Number.isFinite(Number(row.totalScore))
        ) {
          overallRatingSum += Number(row.totalScore);
          overallCount += 1;
        }
      }
      keys.forEach((k) => {
        const v = row.scores[k];
        if (v === null || v === undefined || isUnratedOrNaScore(v)) return;
        sums[k] += toNumber(v, 0);
        counts[k] += 1;
      });
    });

    const denominator = overallCount;
    const averagesByParam = {};
    keys.forEach((k) => {
      averagesByParam[k] = counts[k] ? sums[k] / counts[k] : null;
    });

    const overallFromApi = denominator > 0 ? overallRatingSum / denominator : 0;
    const overallFromParams =
      keys.length > 0
        ? (() => {
            const validAverages = keys
              .map((k) => averagesByParam[k])
              .filter((value) => value !== null && value !== undefined);
            if (!validAverages.length) return 0;
            return validAverages.reduce((s, value) => s + value, 0) / validAverages.length;
          })()
        : 0;
    const overallFromDisplayedParams =
      keys.length > 0
        ? (() => {
            const roundedVisibleAverages = keys
              .map((k) => averagesByParam[k])
              .filter((value) => value !== null && value !== undefined)
              .map((value) => Number(value.toFixed(1)));
            if (!roundedVisibleAverages.length) return 0;
            return (
              roundedVisibleAverages.reduce((sum, value) => sum + value, 0) /
              roundedVisibleAverages.length
            );
          })()
        : 0;
    const summaryRatingCandidates = matchedSummaryRows
      .map((item) => toNumber(item.overallRating, NaN))
      .filter((value) => Number.isFinite(value) && value > 0);
    const overallFromSummary =
      summaryRatingCandidates.length > 0
        ? summaryRatingCandidates.reduce((acc, value) => acc + value, 0) /
          summaryRatingCandidates.length
        : 0;

    const overallRating =
      overallFromApi > 0
        ? overallFromApi
        : overallFromSummary > 0
        ? overallFromSummary
        : overallFromParams > 0
        ? overallFromParams
        : overallFromDisplayedParams;

    return {
      totalEvents: detailTotalCount > sourceRows.length ? detailTotalCount : totalEvents,
      averagesByParam,
      paramKeys: keys,
      overallRating,
      overallOutOf: 5,
    };
  }, [
    strictEmployeeJobRows,
    jobTypeRows,
    reportColumns,
    employeeId,
    jobTypeId,
    selectedJobTypeName,
    summaryRows,
    detailTotalCount,
  ]);

  const tableColumns = useMemo(
    () => (reportColumns.length > 0 ? reportColumns : configuredParameters || []),
    [reportColumns, configuredParameters]
  );

  const ratingTitle = useMemo(() => {
    const selectedPreset = datePresets.find((preset) => preset.value === dateRange);
    const selectedLabel = selectedPreset?.label || 'Overall';
    if (selectedLabel.toLowerCase() === 'all') return 'Overall Rating';
    return `${selectedLabel} Rating`;
  }, [dateRange]);

  const strictEmployeeJobChartRows = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const selectedJobTypeId = String(jobTypeId || '');
    const selectedJobType = normalizeText(selectedJobTypeName);
    const byEmployee = rowsForChart.filter(
      (row) => String(row.employeeId || '') === selectedEmployeeId
    );
    if (!selectedJobTypeId && !selectedJobType) return byEmployee;
    return byEmployee.filter((row) => {
      const rowJobTypeId = String(row.jobTypeId || '');
      if (selectedJobTypeId && rowJobTypeId) return rowJobTypeId === selectedJobTypeId;
      if (selectedJobType) return normalizeText(row.jobType) === selectedJobType;
      return true;
    });
  }, [rowsForChart, selectedEmployeeId, jobTypeId, selectedJobTypeName]);

  const chartRowsInRange = useMemo(
    () =>
      strictEmployeeJobChartRows.filter((row) =>
        isDateInSelectedRange(row.startDate, dateRange, fromDate, toDate)
      ),
    [strictEmployeeJobChartRows, dateRange, fromDate, toDate]
  );

  const matchGraphRows = useMemo(() => {
    /** Include any row with a numeric Average (`totalScore`). `null` = abandoned / no total (not 0.0). */
    const hasPlottableAverage = (row) =>
      row.totalScore !== null &&
      row.totalScore !== undefined &&
      Number.isFinite(Number(row.totalScore));

    const overallAvg = toNumber(summary.overallRating, NaN);
    const avgLine =
      Number.isFinite(overallAvg) && overallAvg > 0 ? overallAvg : null;

    const ordered = [...chartRowsInRange]
      .filter((row) => hasPlottableAverage(row))
      .sort((a, b) => rowSortTimestamp(a.startDate) - rowSortTimestamp(b.startDate));

    return ordered.map((row, index) => {
      const rawTotal = row.totalScore;
      const total =
        rawTotal === null || rawTotal === undefined ? NaN : Number(rawTotal);
      return {
        id: row.id,
        index: index + 1,
        label: `M${index + 1}`,
        matchName: formatMatchNameWithDay(row),
        value: Number.isFinite(total) ? total : null,
        svValue: avgLine,
      };
    });
  }, [chartRowsInRange, summary.overallRating]);

  const buildVisibleRowsPdfBase64 = (rowsForPdf) => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const marginX = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    let cursorY = 14;

    const ratingText = `${ratingTitle}: ${formatOverallRatingValue(summary.overallRating)}/${summary.overallOutOf.toFixed(1)}`;

    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text('Feedback Report', pageWidth / 2, cursorY, { align: 'center' });

    doc.setFontSize(12);
    doc.setTextColor(37, 99, 235);
    doc.text(ratingText, pageWidth - marginX, cursorY + 6, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    cursorY += 12;

    const summaryHeaderRow = ['Parameter', ...summary.paramKeys.map((key) => formatParameterLabel(key))];
    const summaryValueRow = ['Average', ...summary.paramKeys.map((key) =>
      summary.averagesByParam[key] === null || summary.averagesByParam[key] === undefined
        ? 'NA'
        : Number(summary.averagesByParam[key]).toFixed(1)
    )];
    const summaryColumnWidth = (pageWidth - marginX * 2) / Math.max(summaryHeaderRow.length, 1);

    if (summaryHeaderRow.length) {
      autoTable(doc, {
        startY: cursorY,
        head: [summaryHeaderRow],
        body: [summaryValueRow],
        styles: { fontSize: 9, cellWidth: summaryColumnWidth, halign: 'center', valign: 'middle' },
        headStyles: { fillColor: [15, 118, 110], halign: 'center', valign: 'middle' },
        columnStyles: {
          0: { fillColor: [241, 245, 249], fontStyle: 'bold', textColor: [15, 23, 42], halign: 'center' },
        },
        margin: { left: marginX, right: marginX },
      });
      cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;
    }

    autoTable(doc, {
      startY: cursorY,
      head: [[
        'S.No',
        'Match Name',
        'Team Name',
        'Job Type',
        'Start Date',
        ...tableColumns.map((key) => formatParameterLabel(key)),
        'Average',
        'Remarks',
      ]],
      body: rowsForPdf.map((row, index) => [
        index + 1,
        formatMatchNameWithDay(row),
        row.teamName || '-',
        row.jobType,
        formatDate(row.startDate),
        ...tableColumns.map((key) => formatParameterCell(row.scores[key])),
        formatAverageCell(row.totalScore),
        row.remarks,
      ]),
      styles: { fontSize: 8, cellPadding: 2, halign: 'center', valign: 'middle' },
      headStyles: { fillColor: [37, 99, 235], halign: 'center', valign: 'middle' },
      columnStyles: {
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'left' },
      },
      margin: { left: marginX, right: marginX },
      theme: 'grid',
    });

    const dataUri = doc.output('datauristring');
    return dataUri.includes(',') ? dataUri.split(',')[1] : '';
  };

  const handleSendPdfMail = async () => {
    if (!employeeId) {
      toast.error('Please select an employee.');
      return;
    }

    if (!visibleRows.length && !(lastSummaryQueryRef.current && detailTotalCount > 0)) {
      toast.error('No visible report data to send.');
      return;
    }

    try {
      setIsSendingPdfMail(true);

      let rowsForPdf = visibleRows;
      if (lastSummaryQueryRef.current && detailTotalCount > visibleRows.length) {
        const cap = Math.min(2000, Math.max(detailTotalCount, visibleRows.length));
        const q = { ...lastSummaryQueryRef.current, page: 1, limit: cap };
        const [response, eventTeamsResponse] = await Promise.all([
          api.getEmployeeFeedbackSummary(q),
          getEventTeamsCached(),
        ]);
        const { scaledRows: allScaled } = normalizeEmployeeSummaryFetchResult(
          response,
          eventTeamsResponse,
          configuredParameters ?? []
        );
        let list = allScaled.filter((row) => String(row.employeeId || '') === String(employeeId));
        if (selectedJobTypeName) {
          const selectedName = normalizeText(selectedJobTypeName);
          list = list.filter((row) => normalizeText(row.jobType) === selectedName);
        }
        rowsForPdf = list;
      }

      if (!rowsForPdf.length) {
        toast.error('No report rows to include in PDF.');
        return;
      }

      const pdfBase64 = buildVisibleRowsPdfBase64(rowsForPdf);
      if (!pdfBase64) {
        throw new Error('Failed to generate PDF content.');
      }

      const userResponse = await api.getUser(employeeId);
      const userPayload = getNestedPayload(userResponse);
      const recipientEmail = String(
        userPayload?.email || userPayload?.user?.email || ''
      ).trim();
      if (!recipientEmail) {
        throw new Error('Selected employee does not have an email address.');
      }
      const selectedEmployee = employees.find(
        (item) => String(item.id) === String(employeeId)
      );
      const safeEmployeeName = String(selectedEmployee?.name || employeeId)
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-_]/g, '');
      const fileName = `employee-feedback-report-${safeEmployeeName || employeeId}.pdf`;

      await api.sendEmployeeFeedbackPdfEmail({
        employeeId,
        recipientEmail,
        recipientEmails: [recipientEmail],
        to: recipientEmail,
        employeeEmail: recipientEmail,
        fileName,
        pdfBase64,
        filters: {
          jobTypeId,
          dateRange,
          fromDate,
          toDate,
          searchText,
          sortBy: tableSort.key,
          sortOrder: tableSort.direction,
          page: tableCurrentPage,
          limit: tableItemsPerPage,
          detailTotalCount,
        },
      });

      toast.success('PDF report sent successfully.');
    } catch (error) {
      toast.error(error?.message || 'Failed to send PDF report.');
    } finally {
      setIsSendingPdfMail(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '14px',
          padding: '14px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '14px',
            alignItems: 'stretch',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '8px',
                alignItems: 'end',
              }}
            >
            <div style={{ minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>
                Member
              </label>
              <select
                value={employeeId || ''}
                onChange={(event) => setEmployeeId(event.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d0d5dd',
                  borderRadius: '7px',
                  fontSize: '12px',
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Select employee</option>
                {employees.map((item) => (
                  <option key={item.id || item.name} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>
                JOB TYPE
              </label>
              <select
                value={jobTypeId}
                onChange={(event) => {
                  const selectedId = event.target.value;
                  const selectedLabel =
                    event.target.selectedIndex > 0
                      ? event.target.options[event.target.selectedIndex]?.text || ''
                      : '';
                  setJobTypeId(selectedId);
                  setJobTypeFilterName(selectedLabel.trim());
                }}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d0d5dd',
                  borderRadius: '7px',
                  fontSize: '12px',
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">All Job Types</option>
                {jobTypes.map((item) => (
                  <option key={item.id || item.name} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>DATE RANGE</div>
              <select
                value={dateRange}
                onChange={(event) => setDateRange(event.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d0d5dd',
                  borderRadius: '7px',
                  fontSize: '12px',
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                {datePresets.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>SEARCH EVENT</div>
              <input
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search event"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d0d5dd',
                  borderRadius: '7px',
                  fontSize: '12px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

            {dateRange === 'custom' && (
              <div
                style={{
                  marginTop: '2px',
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                  style={{
                    flex: '1 1 140px',
                    minWidth: 0,
                    maxWidth: '200px',
                    padding: '8px 10px',
                    border: '1px solid #d0d5dd',
                    borderRadius: '7px',
                    fontSize: '12px',
                    boxSizing: 'border-box',
                  }}
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                  style={{
                    flex: '1 1 140px',
                    minWidth: 0,
                    maxWidth: '200px',
                    padding: '8px 10px',
                    border: '1px solid #d0d5dd',
                    borderRadius: '7px',
                    fontSize: '12px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            <div
              style={{
                borderRadius: '14px',
                padding: '14px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                minHeight: '120px',
              }}
            >
              <div style={{ fontSize: '11px', opacity: 0.85 }}>{ratingTitle}</div>
              <div style={{ marginTop: '6px', fontSize: '32px', lineHeight: 1, fontWeight: 700 }}>
                {formatOverallRatingValue(summary.overallRating)}
              </div>
              <div style={{ marginTop: '6px', fontSize: '12px', opacity: 0.95 }}>
                <span style={{ fontSize: '14px', fontWeight: 700 }}>
                  ({detailTotalCount} Matches)
                </span>
                {chartLoading ? ' · refreshing chart…' : ''}
              </div>
            </div>
          </div>

          <div
            style={{
              borderRadius: '16px',
              padding: '14px',
              boxSizing: 'border-box',
              minHeight: '220px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              color: '#1e293b',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              background: '#ffffff',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 4px 14px rgba(15, 23, 42, 0.06)',
              border: '1px solid #e2e8f0',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                marginBottom: '10px',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  color: '#334155',
                  fontFamily:
                    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '4px',
                }}
              >
                <span>Member</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span
                    style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      background: '#ef4444',
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                  Match
                </span>
                <span>rating trend (match-wise)</span>
              </div>
            </div>

            {matchGraphRows.length ? (
              <div
                style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}
              >
                <MatchRatingTrendChart
                  rows={matchGraphRows}
                  fillSpace
                  seriesMode="tl"
                  variant="light"
                  labelPrimary="Match"
                  labelSecondary="Average"
                />
              </div>
            ) : (
              <div
                style={{
                  fontSize: '12px',
                  color: '#64748b',
                  padding: '28px 6px',
                  textAlign: 'center',
                  flex: 1,
                }}
              >
                {!employeeId
                  ? 'Select an employee to view match rating trend.'
                  : chartLoading
                  ? 'Loading match trend…'
                  : 'No match ratings for selected filters.'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: '14px',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '14px',
          padding: '14px',
        }}
      >
        {/* {loading && !tableRefreshing && (
          <div style={{ marginTop: '8px', color: '#64748b', fontSize: '12px' }}>Loading report...</div>
        )}
        {!loading && !employeeId && (
          <div style={{ marginTop: '8px', color: '#64748b', fontSize: '12px' }}>
            Select employee to view report data.
          </div>
        )}
        {!loading && errorMessage && (
          <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '12px' }}>{errorMessage}</div>
        )} */}

        <div
          style={{
            marginTop: '12px',
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.max(summary.paramKeys.length, 1)}, minmax(120px, 1fr))`,
            gap: '10px',
          }}
        >
          {summary.paramKeys.length ? (
            summary.paramKeys.map((key) => (
              <div key={key} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                  {key}
                </div>
                <div style={{ color: '#2563eb', marginTop: '4px', fontSize: '26px', lineHeight: 1, fontWeight: 700 }}>
                  {summary.averagesByParam[key] === null || summary.averagesByParam[key] === undefined
                    ? 'NA'
                    : summary.averagesByParam[key].toFixed(1)}
                </div>
              </div>
            ))
          ) : (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', color: '#64748b', fontSize: '12px' }}>
              {employeeId && !loading
                ? 'No feedback parameters in settings and none found in report data.'
                : 'Select an employee to load feedback report.'}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: '16px',
            border: '1px solid #eaecf0',
            borderRadius: '12px',
            width: '100%',
            position: 'relative',
          }}
        >
          <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div
              style={{
                opacity: tableRefreshing ? 0.88 : 1,
                transition: 'opacity 0.2s ease-out',
                pointerEvents: tableRefreshing ? 'none' : 'auto',
              }}
            >
              <table
                style={{
                  width: '100%',
                  minWidth: `${480 + Math.max(tableColumns.length, 1) * 100}px`,
                  tableLayout: 'auto',
                  borderCollapse: 'collapse',
                  whiteSpace: 'nowrap',
                }}
              >
                <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <tr>
                    <th
                      style={{
                        textAlign: 'center',
                        padding: '10px 10px',
                        color: '#f8fafc',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      S.No
                    </th>
                    {renderSortableHeader('matchName', 'Match Name', 'Sort by match name')}
                    {renderSortableHeader('teamName', 'Team Name', 'Sort by team name')}
                    {renderSortableHeader('jobType', 'Job Type', 'Sort by job type')}
                    {renderSortableHeader('startDate', 'Start Date', 'Sort by start date')}
                    {tableColumns.map((colKey) =>
                      renderSortableHeader(
                        `score:${colKey}`,
                        formatParameterLabel(colKey),
                        `Sort by ${formatParameterLabel(colKey)}`
                      )
                    )}
                    {renderSortableHeader('totalScore', 'Average', 'Sort by average score')}
                    {renderSortableHeader('remarks', 'Remarks', 'Sort by remarks')}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length ? (
                    visibleRows.map((row, index) => {
                      return (
                        <tr key={row.id} style={{ borderTop: '1px solid #eaecf0' }}>
                          <td
                            style={{
                              padding: '9px 10px',
                              fontSize: '12px',
                              color: '#344054',
                              textAlign: 'center',
                            }}
                          >
                            {(tableSafePage - 1) * tableItemsPerPage + index + 1}
                          </td>
                          <td
                            style={{
                              padding: '9px 10px',
                              fontSize: '12px',
                              color: '#344054',
                              textAlign: 'left',
                            }}
                          >
                            {formatMatchNameWithDay(row)}
                          </td>
                          <td
                            style={{
                              padding: '9px 10px',
                              fontSize: '12px',
                              color: '#344054',
                              textAlign: 'left',
                            }}
                          >
                            {row.teamName || '-'}
                          </td>
                          <td
                            style={{
                              padding: '9px 10px',
                              fontSize: '12px',
                              color: '#344054',
                              textAlign: 'left',
                            }}
                          >
                            {row.jobType}
                          </td>
                          <td
                            style={{
                              padding: '9px 10px',
                              fontSize: '12px',
                              color: '#344054',
                              textAlign: 'center',
                            }}
                          >
                            {formatDate(row.startDate)}
                          </td>
                          {tableColumns.map((key) => (
                            <td
                              key={key}
                              style={{
                                padding: '9px 10px',
                                fontSize: '12px',
                                color: '#344054',
                                textAlign: 'center',
                              }}
                            >
                              {formatParameterCell(row.scores[key])}
                            </td>
                          ))}
                          <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                            <span
                              style={{
                                background:
                                  row.totalScore === null || row.totalScore === undefined
                                    ? '#f1f5f9'
                                    : '#dbeafe',
                                color:
                                  row.totalScore === null || row.totalScore === undefined
                                    ? '#64748b'
                                    : '#1d4ed8',
                                borderRadius: '999px',
                                padding: '3px 10px',
                                fontWeight: 700,
                                fontSize: '12px',
                              }}
                            >
                              {formatAverageCell(row.totalScore)}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: '9px 10px',
                              fontSize: '12px',
                              color: '#344054',
                              textAlign: 'left',
                            }}
                          >
                            {row.remarks}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6 + tableColumns.length} style={{ textAlign: 'center', color: '#64748b' }}>
                        No feedback entries found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={tableSafePage}
            totalPages={tableTotalPages}
            totalItems={tableTotalItems}
            itemsPerPage={tableItemsPerPage}
            onPageChange={setTableCurrentPage}
            onItemsPerPageChange={(value) => {
              setTableItemsPerPage(value);
              setTableCurrentPage(1);
            }}
            extraEnd={
              <button
                type="button"
                onClick={handleSendPdfMail}
                disabled={
                  !employeeId || (!visibleRows.length && detailTotalCount === 0) || isSendingPdfMail
                }
                style={{
                  border: '1px solid #e3e6e4',
                  background: isSendingPdfMail ? '#e3e6e4' : 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
                  color: '#ffffff',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  fontWeight: 700,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  cursor:
                    !employeeId || (!visibleRows.length && detailTotalCount === 0) || isSendingPdfMail
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    !employeeId || (!visibleRows.length && detailTotalCount === 0) || isSendingPdfMail
                      ? 0.7
                      : 1,
                }}
              >
                {isSendingPdfMail ? 'Sending Feedback Report...' : 'Send Feedback Report'}
              </button>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeFeedbackReportPage;
